import { FilePdf } from '@phosphor-icons/react'
import { Button, Select } from 'antd'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router-dom'

import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import useSearchParams from '@/hooks/useSearchParams'
import { useLoadFormMeasures } from '@/queries/measures/useLoadFormMeasures'
import { useLoadMeasuresByFormAndClientId } from '@/queries/measures/useLoadMeasuresByFormAndClientId'
import { ButtonGroup } from '@/ui/atoms/ButtonGroup'
import Tooltip from '@/ui/atoms/Tooltip'
import cn from '@/utils/tailwind/cn'

import { AddNewScoreForm } from './Components/AddNewScoreForm'
import { InstructionsMeasures } from './Components/InstructionsMeasures/InstructionsMeasures'
import { MeasureBadge } from './Components/MeasureBadge'
import { MetricCard } from './Components/MetricCard'
import { PrintMeasuresPDFModal } from './Components/PrintMeasuresPDFModal/PrintMeasuresPDFModal'
import { FAKE_MEASURES_DATA } from './mapped'
import { ChartView } from './Section/ChartView'
import { TableView } from './Section/TableView'
import { getCalculateMeasuresByQuestionId } from './utils/getCalculateMeasuresByQuestionId'
import { getCurrentQuestionById } from './utils/getCurrentQuestionById'
import { transformLevelsToCategories } from './utils/transformLevelsToCategories'

export const MeasuresView = () => {
  const { clientId } = useParams<{ clientId?: string }>()
  const searchParams = useSearchParams()
  const updateParams = useUpdateURLSearchParams()
  const canPrintPDF = Boolean(searchParams.get('print') === 'true' || '')

  const formId = searchParams.get('formId')

  const isTableView = canPrintPDF ? false : searchParams.get('view') === 'table'

  const {
    data: measureData,
    isLoading: isLoadingMeasure,
    isSuccess: isSuccessMeasure,
  } = useLoadMeasuresByFormAndClientId(formId || '', clientId || '')

  const { data, isLoading, isSuccess } = useLoadFormMeasures()

  let formMeasuresData = FAKE_MEASURES_DATA()

  if (isSuccess) {
    formMeasuresData = data || []
  }

  const formOptions = formMeasuresData.results.map((item) => ({
    label: item.name,
    value: item.id,
  }))

  const questionOptions = measureData?.questions?.map((item) => ({
    label: item.text,
    value: item.id,
  }))

  const questionId = searchParams.get('question')

  let hasData = false

  if (isSuccessMeasure && questionId && !isLoading && !isLoadingMeasure)
    hasData = true

  const calculatedMeasures = getCalculateMeasuresByQuestionId({
    data: measureData,
    questionId,
  })

  const currentQuestion = getCurrentQuestionById({
    data: measureData,
    questionId,
  })
  const levels = transformLevelsToCategories(currentQuestion?.levels)
  const hasLevels = Boolean(currentQuestion?.levels?.length)
  const latestLevel = levels?.(calculatedMeasures.latestScore)

  useEffect(() => {
    if (measureData && !isLoadingMeasure) {
      const firstQuestionId = measureData?.questions?.[0].id
      updateParams(['question', String(firstQuestionId)])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingMeasure, measureData])

  return (
    <>
      <Helmet>
        <title>Measures - Client/Zentake</title>
      </Helmet>
      <div className={cn('flex flex-col gap-12', canPrintPDF && 'blur-lg')}>
        <div className="relative grid w-full grid-cols-[2fr_1fr] gap-4">
          <div className="flex w-full flex-col items-start">
            <div className="grid w-full max-w-[826.66px] grid-cols-[1.2fr_1.5fr] gap-4">
              <Select
                allowClear
                className="h-[40px] w-full max-w-[360.28px] [&_.ant-select-selection-wrap]:mt-0.5"
                disabled={isLoadingMeasure || isLoading}
                key={questionOptions?.length}
                loading={isLoadingMeasure}
                onChange={(value: string) => updateParams(['question', value])}
                options={questionOptions || []}
                placeholder="Select Tracked Question"
                value={
                  currentQuestion
                    ? ({
                        label: currentQuestion?.text,
                        value: currentQuestion?.id,
                      } as unknown as string)
                    : undefined
                }
              />

              <Tooltip
                label="Click to change the Assessment"
                mouseEnterDelay={0.7}
                placement="topRight"
              >
                <Select
                  allowClear
                  className="!h-10 w-full max-w-[450.36px] [&_.ant-select-selection-item]:font-bold"
                  defaultValue={formId}
                  disabled={isLoading}
                  loading={isLoading}
                  onChange={(value: string) => updateParams(['formId', value])}
                  options={formOptions || []}
                  placeholder="Select an Assessment"
                />
              </Tooltip>
            </div>
            {hasData ? (
              <div className="mt-3 grid h-full w-full grid-cols-3 gap-4">
                <MetricCard.Root>
                  <MetricCard.Title>Latest Score</MetricCard.Title>
                  <MetricCard.Number>
                    {calculatedMeasures.latestScore}
                  </MetricCard.Number>
                  {hasLevels ? (
                    <MeasureBadge
                      className="absolute bottom-2 right-2"
                      style={{
                        background: `${latestLevel?.color}30`,
                        color: latestLevel?.color,
                      }}
                    >
                      {latestLevel?.label}
                    </MeasureBadge>
                  ) : null}
                </MetricCard.Root>
                <MetricCard.Root>
                  <MetricCard.Title>Average</MetricCard.Title>
                  <MetricCard.Number>
                    {calculatedMeasures.average}
                  </MetricCard.Number>
                </MetricCard.Root>
                <MetricCard.Root>
                  <MetricCard.Title>Total Assessments</MetricCard.Title>
                  <MetricCard.Number>
                    {calculatedMeasures.totalAssessments}
                  </MetricCard.Number>
                </MetricCard.Root>
              </div>
            ) : null}
          </div>
          {hasData ? (
            <>
              <Tooltip mouseEnterDelay={0.4} title="Generate PDF Page">
                <Button
                  className="absolute -top-[58px] right-0 z-[2] w-10 bg-gray-300/20 !px-0 !text-gray-800 hover:!bg-gray-300/40 hover:!text-gray-500"
                  onClick={() => updateParams(['print', 'true'])}
                  type="link"
                >
                  <FilePdf className="text-2xl" />
                </Button>
              </Tooltip>
              <div className="relative flex flex-col justify-between gap-2 rounded border border-gray-50 bg-white pb-3">
                <h2 className="w-full border-b border-dashed border-gray-200 py-2.5 text-center text-base font-bold text-gray-800">
                  Add New Score
                </h2>
                <AddNewScoreForm className="px-3" />
              </div>
            </>
          ) : null}
        </div>
        {hasData ? (
          <>
            <div className="flex w-full flex-col items-start justify-start gap-2">
              <ButtonGroup.Root className="-mt-3 mb-1.5 !border-primary/10 bg-primary/10">
                <ButtonGroup.Item
                  className={cn('h-[30px] text-blue-900', {
                    'text-gray-900': !isTableView,
                  })}
                  isActive={!isTableView}
                  onClick={() => updateParams(['view', 'chart'])}
                >
                  Chart View
                </ButtonGroup.Item>
                <ButtonGroup.Item
                  className={cn('h-[30px] text-blue-900', {
                    'text-gray-900': isTableView,
                  })}
                  isActive={isTableView}
                  onClick={() => updateParams(['view', 'table'])}
                >
                  Table View
                </ButtonGroup.Item>
              </ButtonGroup.Root>
              <ChartView className={cn(isTableView && 'hidden')} />
              {isTableView ? <TableView /> : null}
            </div>
            <InstructionsMeasures />
          </>
        ) : null}
        {!hasData ? (
          <div className="flex w-full flex-col items-center justify-center gap-1 py-10">
            <h1 className="text-lg font-bold text-gray-700">
              Select an Assessment and a Question to view screening scores
            </h1>
            <span className="text-sm text-gray-500">
              Use the dropdown menu above to select an Assessment and a Question
            </span>
          </div>
        ) : null}
      </div>
      {canPrintPDF ? (
        <PrintMeasuresPDFModal
          {...{
            measureData,
            question: currentQuestion,
            ...calculatedMeasures,
            level: latestLevel,
          }}
        />
      ) : null}
    </>
  )
}
